import React, { useEffect, useState } from 'react';
import { useReportService } from '../../services/ReportService';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Stack, MultiSelect, Text, Divider } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { DateInput } from '@mantine/dates';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { ProductListFragment } from '../../graphql/graphql';
import { useProductService } from '../../services/ProductService';
import z from 'zod';

const validationSchema = z.object({
	productIds: z.array(z.string()).min(1, 'At least one product must be selected'),
	purchaseDateFrom: z.null().or(z.coerce.date()),
	purchaseDateTo: z.null().or(z.coerce.date()),
});

type ProductPurchasesReportForm = {
	productIds: string[];
	purchaseDateFrom: Date | null;
	purchaseDateTo: Date | null;
};

export type ProductPurchasesReportProps = {
	className?: string;
	onReportGenerated: (report: Blob, filenamePrefix: string) => void;
};

export default function ProductPurchasesReport(props: ProductPurchasesReportProps) {
	const { className, onReportGenerated } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const [products, setProducts] = useState<ProductListFragment[]>([]);

	const reportService = useReportService();
	const productService = useProductService();

	useEffect(() => {
		const loader = addLoader();
		const request = productService.getAll();
		request.response
			.then((products) => {
				setProducts(products);
			})
			.catch((err) => {
				NotificationUtils.showError(err as Error, 'Failed to load products');
			})
			.finally(() => {
				removeLoader(loader);
			});
	}, [addLoader, removeLoader, productService]);

	const form = useForm<ProductPurchasesReportForm>({
		initialValues: {
			productIds: [],
			purchaseDateFrom: null,
			purchaseDateTo: null,
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: ProductPurchasesReportForm) => {
		const loader = addLoader();
		try {
			const report = await reportService.getProductPurchasesReport({
				productIds: values.productIds,
				purchaseDateFrom: values.purchaseDateFrom ?? undefined,
				purchaseDateTo: values.purchaseDateTo ?? undefined,
			});
			onReportGenerated(report, 'product_purchases_report');
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to generate report');
		} finally {
			removeLoader(loader);
		}
	};

	const productData = products.map((p) => ({
		value: p.id,
		label: p.shortName,
	}));

	return (
		<div className={`ExportOptions ${className ?? ''}`}>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack maw={640}>
					<Text size="sm">
						This report will generate a CSV file containing all users and their purchases for the selected
						products and filters.
					</Text>
					<MultiSelect
						{...form.getInputProps('productIds')}
						data={productData}
						label="Product(s)"
						description="Select one or more products"
						placeholder={isLoading ? 'Loading...' : ''}
						disabled={isLoading}
						withAsterisk
					/>
					<Divider />
					<Text size="sm">You may optionally specify a date range to filter by the purchase date</Text>
					<DateInput
						{...form.getInputProps('purchaseDateFrom')}
						label="Purchase Date From"
						description="Lower date bound for when the purchase was made"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<DateInput
						{...form.getInputProps('purchaseDateTo')}
						label="Purchase Date To"
						description="Upper bound for when the purchase was made"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<Divider />
					<Button type="submit" className="flex ac jc gxs" loading={isLoading}>
						<span>Generate Report (CSV)</span>
					</Button>
				</Stack>
			</form>
		</div>
	);
}
